import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api, { User, UserRole, ADMIN_ROLES, rolePermissionsApi, RolePermissionsGrid, PERMISSION_KEYS, teamsApi, Team } from '@/lib/api'
// teamsApi includes: list, create, rename, delete, addMember, removeMember, setManager, clearManager
import { useAuth } from '@/contexts/AuthContext'
import SlideOver from '@/components/SlideOver'
import {
  Plus, Shield, Key, AlertCircle, Mail, Save, RotateCcw, CheckCircle2, Lock,
  ChevronDown, ChevronRight, Star, Trash2, Pencil, Users2, Network
} from 'lucide-react'

const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  director: 'Director',
  sales_manager: 'Sales Mgr',
  asst_sales_manager: 'Asst Sales Mgr',
  sales_executive: 'Sales Exec',
  chief_engineer: 'Chief Eng',
  operation_manager: 'Op. Mgr',
  engineer: 'Engineer',
  technician: 'Technician',
  finance: 'Finance',
  procurement: 'Procurement',
  storekeeper: 'Storekeeper',
  hr: 'HR',
}

const ROLE_BADGE_COLORS: Record<UserRole, string> = {
  super_admin: 'bg-violet-100 text-violet-700',
  admin: 'bg-purple-100 text-purple-700',
  director: 'bg-indigo-100 text-indigo-700',
  sales_manager: 'bg-red-100 text-red-700',
  asst_sales_manager: 'bg-orange-100 text-orange-700',
  sales_executive: 'bg-amber-100 text-amber-700',
  chief_engineer: 'bg-blue-100 text-blue-700',
  operation_manager: 'bg-teal-100 text-teal-700',
  engineer: 'bg-cyan-100 text-cyan-700',
  technician: 'bg-sky-100 text-sky-700',
  finance: 'bg-emerald-100 text-emerald-700',
  procurement: 'bg-orange-100 text-orange-800',
  storekeeper: 'bg-lime-100 text-lime-700',
  hr: 'bg-pink-100 text-pink-700',
}

const PERMISSION_LABELS: Record<string, string> = {
  crm_view: 'View Contacts & Deals',
  crm_write: 'Create / Edit Contacts & Deals',
  customer_db_write: 'Create / Edit Customer Database',
  project_bank_create: 'Create Projects',
  project_bank_edit: 'Edit Projects',
  sr_ss_create: 'Create Service Requests & Surveys',
  sr_ss_edit_all: 'Edit Any SR/SS (not just own)',
  billing: 'Bill on Service Requests',
  handover_write: 'Create / Edit Handover Forms',
  finance_view_all: 'View All Finance Submissions',
  finance_approve: 'Approve / Reject Finance Submissions',
  analytics_finance: 'See Finance Summary in Analytics',
  weekly_report_view_all: 'View All Staff Weekly Reports',
  activity_logs_view: 'Access Activity Logs',
}

const ALL_ROLES_LIST: UserRole[] = [
  'super_admin', 'admin', 'director',
  'sales_manager', 'asst_sales_manager', 'sales_executive',
  'chief_engineer', 'operation_manager', 'engineer', 'technician',
  'finance', 'procurement', 'storekeeper', 'hr',
]

function getAssignableRoles(actorRole: UserRole): UserRole[] {
  if (actorRole === 'super_admin') return ALL_ROLES_LIST
  if (actorRole === 'admin') return ALL_ROLES_LIST.filter(r => !ADMIN_ROLES.includes(r))
  return []
}

function canManageUser(actorRole: UserRole, targetRole: UserRole): boolean {
  if (actorRole === 'super_admin') return true
  if (actorRole === 'admin') return !ADMIN_ROLES.includes(targetRole)
  return false
}

// ─── Teams Tab ───────────────────────────────────────────────────────────────

const ENGINEERING_MEMBER_ROLES: UserRole[] = ['operation_manager', 'engineer', 'technician']
const TEAM_MANAGER_ROLES: UserRole[] = ['sales_manager', 'asst_sales_manager']

function TeamsTab({ allUsers }: { allUsers: User[] }) {
  const queryClient = useQueryClient()
  const [expandedTeam, setExpandedTeam] = useState<number | null>(null)
  const [newTeamName, setNewTeamName] = useState('')
  const [renamingId, setRenamingId] = useState<number | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [assignUser, setAssignUser] = useState<Record<number, string>>({}) // teamId → userId string
  const [teamError, setTeamError] = useState('')
  // Bulk assign state: { userId → teamId }
  const [bulkAssign, setBulkAssign] = useState<Record<number, string>>({})
  const [bulkPending, setBulkPending] = useState<Set<number>>(new Set())

  const { data: teams = [], isLoading: teamsLoading, isError: teamsError } = useQuery<Team[]>({
    queryKey: ['teams'],
    queryFn: teamsApi.list,
    staleTime: 10000,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['teams'] })
    queryClient.invalidateQueries({ queryKey: ['org-chart'] })
    queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  const createTeam = useMutation({
    mutationFn: (name: string) => teamsApi.create(name),
    onSuccess: () => { setNewTeamName(''); setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to create team'),
  })

  const renameTeam = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => teamsApi.rename(id, name),
    onSuccess: () => { setRenamingId(null); setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to rename team'),
  })

  const deleteTeam = useMutation({
    mutationFn: (id: number) => teamsApi.delete(id),
    onSuccess: () => { setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to delete team'),
  })

  const addMember = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: number }) => teamsApi.addMember(teamId, userId),
    onSuccess: (_, { teamId }) => { setAssignUser(p => ({ ...p, [teamId]: '' })); setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to add member'),
  })

  const removeMember = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: number }) => teamsApi.removeMember(teamId, userId),
    onSuccess: () => { setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to remove member'),
  })

  const setManager = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: number; userId: number }) => teamsApi.setManager(teamId, userId),
    onSuccess: () => { setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to set manager'),
  })

  const clearManager = useMutation({
    mutationFn: (teamId: number) => teamsApi.clearManager(teamId),
    onSuccess: () => { setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to clear manager'),
  })

  const setSupervisor = useMutation({
    mutationFn: ({ teamId, userId, supervisorId }: { teamId: number; userId: number; supervisorId: number | null }) =>
      teamsApi.setSupervisor(teamId, userId, supervisorId),
    onSuccess: () => { setTeamError(''); invalidate() },
    onError: (e: any) => setTeamError(e.response?.data?.detail || 'Failed to update supervisor'),
  })

  // Team assignment is available to every active account, with engineering
  // divisions intentionally limited to the three engineering job roles.
  const activeUsers = allUsers.filter(u => u.is_active)
  const activeEngineeringUsers = activeUsers.filter(u => ENGINEERING_MEMBER_ROLES.includes(u.role))
  const activeDivisionHeads = activeUsers.filter(u => u.role === 'operation_manager')
  const activeChiefEngineers = activeUsers.filter(u => u.role === 'chief_engineer')
  const isEngineeringDivision = (team: Team) =>
    Boolean(team.division_title) || team.name.startsWith('Engineering —')
  const isEngineeringDepartment = (team: Team) =>
    team.name.trim().toLowerCase() === 'engineering department' || Boolean(team.divisions?.length)

  // Unassigned active users
  const assignedIds = new Set(teams.flatMap(t => t.members.map(m => m.id)))
  const unassignedUsers = activeUsers.filter(u => !assignedIds.has(u.id))

  // All active users available to move into a given team (including those already in other teams)
  const availableForTeam = (teamId: number) => {
    const targetTeam = teams.find(t => t.id === teamId)
    const candidates = targetTeam && isEngineeringDivision(targetTeam) ? activeEngineeringUsers : activeUsers
    return candidates.filter(u => {
      const memberIds = new Set(targetTeam?.members.map(m => m.id) || [])
      return !memberIds.has(u.id)
    })
  }

  // Bulk-assign a single user to their chosen team
  async function handleBulkAssignOne(userId: number) {
    const teamIdStr = bulkAssign[userId]
    if (!teamIdStr) return
    setBulkPending(p => new Set(p).add(userId))
    try {
      await teamsApi.addMember(Number(teamIdStr), userId)
      setBulkAssign(p => { const n = { ...p }; delete n[userId]; return n })
      invalidate()
    } catch (e: any) {
      setTeamError(e.response?.data?.detail || 'Failed to assign user')
    } finally {
      setBulkPending(p => { const n = new Set(p); n.delete(userId); return n })
    }
  }

  if (teamsLoading) return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800" />
    </div>
  )

  if (teamsError) return (
    <div className="flex items-center gap-2 bg-amber-50 text-amber-700 text-sm px-4 py-3 rounded-lg border border-amber-200">
      <AlertCircle size={16} className="shrink-0" />
      <span>Teams could not be loaded. The database migration for teams may not have been applied yet. Run <code className="bg-amber-100 px-1 rounded">docs/sql-migrations/task86-teams.sql</code> in the Supabase SQL Editor first.</span>
    </div>
  )

  return (
    <div className="space-y-4">
      {teamError && (
        <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
          <AlertCircle size={16} className="shrink-0" /><span>{teamError}</span>
        </div>
      )}

      {teams.length > 0 && (
        <div className="flex items-start gap-2 bg-blue-50 text-blue-800 text-sm px-4 py-3 rounded-xl border border-blue-200">
          <Users2 size={16} className="shrink-0 mt-0.5" />
          <span>
            <strong>A team head is a separate designation from a job role.</strong> Engineering division heads may be selected from any active Operation Manager, including one not yet in that division. Project Manager (Yangon), Operations Manager (ELV), and Assistant Operations Manager (Branch) all use the <strong>Operation Manager</strong> permission role. Managers read only their assigned engineer and technician reporting branches; the Chief Engineer reads all Engineering managers, engineers, and technicians.
          </span>
        </div>
      )}

      {/* Unassigned CRM users — bulk-assign panel */}
      {teams.length > 0 && unassignedUsers.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertCircle size={15} className="text-amber-600 shrink-0" />
            <span className="text-sm font-semibold text-amber-800">
              {unassignedUsers.length} active user{unassignedUsers.length !== 1 ? 's' : ''} not yet assigned to a team
            </span>
          </div>
          <div className="space-y-2">
            {unassignedUsers.map(u => (
              <div key={u.id} className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className="w-7 h-7 rounded-full bg-amber-200 flex items-center justify-center text-xs font-semibold text-amber-800 shrink-0">
                    {(u.full_name || u.username || '?')[0].toUpperCase()}
                  </div>
                  <span className="text-sm text-slate-800 font-medium truncate">{u.full_name || u.username}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${ROLE_BADGE_COLORS[u.role] || 'bg-slate-100 text-slate-600'}`}>
                    {ROLE_DISPLAY_NAMES[u.role] || u.role}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <select
                    value={bulkAssign[u.id] || ''}
                    onChange={e => setBulkAssign(p => ({ ...p, [u.id]: e.target.value }))}
                    className="px-2 py-1 text-xs border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-700"
                  >
                    <option value="">Pick team…</option>
                    {teams.filter(t => !isEngineeringDivision(t) || ENGINEERING_MEMBER_ROLES.includes(u.role)).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button
                    disabled={!bulkAssign[u.id] || bulkPending.has(u.id)}
                    onClick={() => handleBulkAssignOne(u.id)}
                    className="px-2.5 py-1 text-xs bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50"
                  >
                    {bulkPending.has(u.id) ? '…' : 'Assign'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {unassignedUsers.length === 0 && teams.length > 0 && activeUsers.length > 0 && (
        <div className="flex items-center gap-2 bg-green-50 text-green-700 text-sm px-4 py-3 rounded-xl border border-green-200">
          <CheckCircle2 size={15} className="shrink-0" />
          <span>All active users are assigned to teams.</span>
        </div>
      )}

      {/* Create team */}
      <form
        onSubmit={e => { e.preventDefault(); if (newTeamName.trim()) createTeam.mutate(newTeamName.trim()) }}
        className="flex gap-2"
      >
        <input
          value={newTeamName}
          onChange={e => setNewTeamName(e.target.value)}
          placeholder="New team name…"
          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
        />
        <button
          type="submit"
          disabled={!newTeamName.trim() || createTeam.isPending}
          className="flex items-center gap-1.5 px-4 py-2 bg-red-800 text-white text-sm rounded-lg hover:bg-red-900 disabled:opacity-50"
        >
          <Plus size={14} /> Create Team
        </button>
      </form>

      {teams.length === 0 && (
        <div className="text-center text-sm text-slate-400 py-10 bg-white rounded-xl border border-slate-200">
          No teams yet. Create one above to start organising your CRM users.
        </div>
      )}

      {teams.map(team => {
        const isExpanded = expandedTeam === team.id
        const isRenaming = renamingId === team.id
        const selectedUserId = assignUser[team.id] || ''
        const pickableUsers = availableForTeam(team.id)
        const engineeringDivision = isEngineeringDivision(team)
        const engineeringDepartment = isEngineeringDepartment(team)
        const engineeringManaged = engineeringDivision || engineeringDepartment
        const divisionTitle = team.division_title || team.name
        const headTitle = engineeringDepartment ? 'Chief Engineer' : divisionTitle
        const managerCandidates = engineeringDivision
          ? activeDivisionHeads
          : engineeringDepartment
            ? activeChiefEngineers
            : team.members.filter(member => member.is_active && TEAM_MANAGER_ROLES.includes(member.role))
        const hasStaleManager = team.manager_id !== null && team.manager === null

        return (
          <div key={team.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            {/* Team header */}
            <div className="flex items-center justify-between px-4 py-3 gap-3">
              <button
                onClick={() => setExpandedTeam(isExpanded ? null : team.id)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
              >
                {isExpanded ? <ChevronDown size={16} className="text-slate-400 shrink-0" /> : <ChevronRight size={16} className="text-slate-400 shrink-0" />}
                <span className="font-semibold text-slate-900">{team.name}</span>
                <span className="text-xs text-slate-500 shrink-0">{team.member_count} member{team.member_count !== 1 ? 's' : ''}</span>
                {team.manager && (
                  <span className="text-xs text-slate-400 truncate hidden sm:inline">
                    · {engineeringManaged ? `${headTitle}: ` : 'Mgr: '}{team.manager.full_name}
                  </span>
                )}
                {!team.manager && (
                  <span className="text-xs text-amber-600 truncate hidden sm:inline">
                    · {engineeringManaged ? `${headTitle} not assigned` : 'Manager not designated'}
                  </span>
                )}
              </button>
              {!engineeringManaged && <div className="flex gap-1 shrink-0">
                <button
                  onClick={() => { setRenamingId(isRenaming ? null : team.id); setRenameValue(team.name) }}
                  className="p-1.5 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-100"
                  title="Rename"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => {
                    if (confirm(`Delete team "${team.name}"? All members will be unassigned.`)) {
                      deleteTeam.mutate(team.id)
                    }
                  }}
                  className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50"
                  title="Delete team"
                >
                  <Trash2 size={14} />
                </button>
              </div>}
            </div>

            {/* Rename form */}
            {isRenaming && (
              <form
                onSubmit={e => { e.preventDefault(); if (renameValue.trim()) renameTeam.mutate({ id: team.id, name: renameValue.trim() }) }}
                className="px-4 pb-3 flex gap-2 border-t border-slate-100 pt-3"
              >
                <input
                  value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
                  autoFocus
                />
                <button type="submit" disabled={!renameValue.trim() || renameTeam.isPending}
                  className="px-3 py-1.5 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50">
                  Save
                </button>
                <button type="button" onClick={() => setRenamingId(null)}
                  className="px-3 py-1.5 text-sm border border-slate-300 rounded-lg hover:bg-slate-50">
                  Cancel
                </button>
              </form>
            )}

            {/* Expanded content */}
            {isExpanded && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-3">
                {engineeringManaged ? (
                  <div className={`rounded-lg border px-3 py-3 ${
                    team.manager ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-amber-50 border-amber-200 text-amber-900'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold">{headTitle}{engineeringDivision ? ' · Division head' : ' · Department head'}</p>
                        <p className="text-[11px] mt-0.5 opacity-80">
                          {team.manager
                            ? `${team.manager.full_name || team.manager.username} is designated.`
                            : engineeringDivision
                              ? 'Choose an active Operation Manager. They do not need to be a current division member.'
                              : 'Choose an active Chief Engineer to lead the department.'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          value={team.manager_id ?? ''}
                          onChange={e => { if (e.target.value) setManager.mutate({ teamId: team.id, userId: Number(e.target.value) }) }}
                          disabled={setManager.isPending}
                          aria-label={`${headTitle} for ${team.name}`}
                          className="min-w-0 flex-1 sm:w-64 px-2.5 py-2 text-xs border border-slate-300 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-red-700"
                        >
                          <option value="">Select an active {engineeringDivision ? 'Operation Manager' : 'Chief Engineer'}…</option>
                          {managerCandidates.map(candidate => (
                            <option key={candidate.id} value={candidate.id}>{candidate.full_name || candidate.username} · {engineeringDivision ? 'Operation Manager' : 'Chief Engineer'}</option>
                          ))}
                        </select>
                        {team.manager_id !== null && (
                          <button
                            onClick={() => clearManager.mutate(team.id)}
                            disabled={clearManager.isPending}
                            className="px-2.5 py-2 text-xs border border-slate-300 text-slate-600 rounded-lg hover:bg-white disabled:opacity-50"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : !team.manager && (
                  <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs ${
                    hasStaleManager
                      ? 'bg-red-50 border-red-200 text-red-700'
                      : 'bg-amber-50 border-amber-200 text-amber-800'
                  }`}>
                    <AlertCircle size={14} className="shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold">
                        {hasStaleManager ? 'Manager assignment is invalid' : 'No team manager designated'}
                      </p>
                      {hasStaleManager ? (
                        <p className="mt-0.5">
                          The saved manager no longer exists. Select an active manager below to repair this team.
                        </p>
                      ) : managerCandidates.length === 1 ? (
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          <span>
                            {managerCandidates[0].full_name || managerCandidates[0].username} has a manager role, but is not yet designated as this team’s manager.
                          </span>
                          <button
                            onClick={() => setManager.mutate({ teamId: team.id, userId: managerCandidates[0].id })}
                            disabled={setManager.isPending}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-amber-700 text-white rounded-md font-medium hover:bg-amber-800 disabled:opacity-50"
                          >
                            <Star size={11} /> Set as Manager
                          </button>
                        </div>
                      ) : managerCandidates.length > 1 ? (
                        <p className="mt-0.5">
                          Multiple manager-role users are on this team. Choose the correct manager using the member list below.
                        </p>
                      ) : (
                        <p className="mt-0.5">
                        Add a Sales Manager or Assistant Sales Manager to this team, then designate them below.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Members list */}
                {team.members.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No members yet. Assign users below.</p>
                ) : (
                  <div className="space-y-1.5">
                    {team.members.map(member => {
                      const isManagerMember = member.id === team.manager_id
                      const supervisorPool = engineeringDivision && team.manager
                        ? [...team.members.filter(candidate => candidate.id !== team.manager_id), team.manager]
                        : team.members
                      const supervisorCandidates = supervisorPool
                        .filter(candidate => candidate.id !== member.id && candidate.is_active)
                        .sort((a, b) => (a.full_name || a.username).localeCompare(b.full_name || b.username))
                      const hasSupervisor = !!member.reports_to_id && supervisorCandidates.some(candidate => candidate.id === member.reports_to_id)
                      return (
                        <div key={member.id} className={`flex items-center justify-between py-2 px-3 rounded-lg gap-3 ${
                          !isManagerMember && !hasSupervisor ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50'
                        }`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600 shrink-0">
                              {(member.full_name || member.username || '?')[0].toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <span className="text-sm font-medium text-slate-900 truncate block">{member.full_name || member.username}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${ROLE_BADGE_COLORS[member.role] || 'bg-slate-100 text-slate-600'}`}>
                                {ROLE_DISPLAY_NAMES[member.role] || member.role}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                            {isManagerMember ? (
                              <span className="flex items-center gap-1">
                                  <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                                    <Star size={11} fill="currentColor" /> {engineeringManaged ? headTitle : 'Manager'}
                                </span>
                                <button
                                  onClick={() => clearManager.mutate(team.id)}
                                  className="text-xs px-2 py-0.5 border border-slate-300 text-slate-500 rounded-full hover:bg-slate-100"
                                  title="Remove manager designation (keeps them in team)"
                                >
                                  Remove
                                </button>
                              </span>
                            ) : (
                              <div className="flex items-center gap-1.5">
                                {(!engineeringManaged || (engineeringDivision
                                  ? member.role === 'operation_manager'
                                  : member.role === 'chief_engineer')) && (
                                  <button
                                    onClick={() => setManager.mutate({ teamId: team.id, userId: member.id })}
                                    className="text-xs px-2 py-0.5 border border-slate-300 text-slate-600 rounded-full hover:bg-slate-100"
                                    title={engineeringDivision ? `Designate as ${divisionTitle}` : 'Set as department head'}
                                  >
                                    {engineeringDivision ? 'Set as head' : 'Set Head'}
                                  </button>
                                )}
                                <label className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 hidden md:block">Reports to</label>
                                <select
                                  value={hasSupervisor ? String(member.reports_to_id) : ''}
                                  onChange={e => setSupervisor.mutate({
                                    teamId: team.id,
                                    userId: member.id,
                                    supervisorId: e.target.value ? Number(e.target.value) : null,
                                  })}
                                  disabled={setSupervisor.isPending}
                                  aria-label={`Direct supervisor for ${member.full_name || member.username}`}
                                  className={`max-w-[11rem] px-2 py-1 text-xs border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-700 ${
                                    hasSupervisor ? 'border-slate-300 text-slate-700' : 'border-amber-300 text-amber-800 font-medium'
                                  }`}
                                >
                                  <option value="">Unassigned supervisor</option>
                                  {supervisorCandidates.map(candidate => (
                                    <option key={candidate.id} value={candidate.id}>
                                      {candidate.full_name || candidate.username}{candidate.id === team.manager_id ? ' · Head' : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <button
                              onClick={() => {
                                if (confirm(`Remove ${member.full_name} from ${team.name}?`)) {
                                  removeMember.mutate({ teamId: team.id, userId: member.id })
                                }
                              }}
                              className="p-1 text-slate-400 hover:text-red-600 rounded hover:bg-red-50"
                              title="Remove from team"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Assign / move member */}
                {pickableUsers.length > 0 && (
                  <div className="flex gap-2 pt-1">
                    <select
                      value={selectedUserId}
                      onChange={e => setAssignUser(p => ({ ...p, [team.id]: e.target.value }))}
                      className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-red-700"
                    >
                      <option value="">Add / move a user…</option>
                      {pickableUsers.map(u => {
                        const currentTeam = teams.find(t => t.members.some(m => m.id === u.id))
                        return (
                          <option key={u.id} value={u.id}>
                            {u.full_name || u.username} ({ROLE_DISPLAY_NAMES[u.role] || u.role}){currentTeam ? ` — from ${currentTeam.name}` : ''}
                          </option>
                        )
                      })}
                    </select>
                    <button
                      disabled={!selectedUserId || addMember.isPending}
                      onClick={() => {
                        if (selectedUserId) addMember.mutate({ teamId: team.id, userId: Number(selectedUserId) })
                      }}
                      className="px-3 py-1.5 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50"
                    >
                      + Add
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function UserManagement() {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const initialTab = (searchParams.get('tab') as 'users' | 'permissions' | 'teams') || 'users'
  const [activeTab, setActiveTab] = useState<'users' | 'permissions' | 'teams'>(
    ['users', 'permissions', 'teams'].includes(initialTab) ? initialTab : 'users'
  )

  const [selected, setSelected] = useState<User | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [showInvite, setShowInvite] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const [showTransfer, setShowTransfer] = useState(false)
  const [ownership, setOwnership] = useState<any>(null)
  const [replacementUserId, setReplacementUserId] = useState('')
  const [editMode, setEditMode] = useState(false)
  const [form, setForm] = useState<any>({})
  const [newPassword, setNewPassword] = useState('')
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')

  const [localGrid, setLocalGrid] = useState<RolePermissionsGrid | null>(null)
  const [permSaveMsg, setPermSaveMsg] = useState('')
  const [permError, setPermError] = useState('')

  const myRole = currentUser?.role as UserRole
  const assignableRoles = getAssignableRoles(myRole)
  const defaultRole = assignableRoles.includes('sales_executive') ? 'sales_executive' : assignableRoles[0] || 'sales_executive'

  const { data: users, isLoading, isError: usersError } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => api.get('/users').then(r => r.data),
  })

  const { data: permData, isLoading: permLoading } = useQuery({
    queryKey: ['role-permissions'],
    queryFn: () => rolePermissionsApi.get(),
    enabled: activeTab === 'permissions',
    staleTime: 30000,
  })

  useEffect(() => {
    if (permData?.grid && localGrid === null) {
      setLocalGrid(JSON.parse(JSON.stringify(permData.grid)))
    }
  }, [permData])

  const createUserDirect = useMutation({
    mutationFn: (data: any) => api.post('/users/create-direct', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowCreate(false); setError('')
      setSuccessMsg('User created successfully. They can now log in with the username and password you set.')
      setTimeout(() => setSuccessMsg(''), 8000)
      setForm({})
    },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to create user') },
  })

  const inviteUser = useMutation({
    mutationFn: (data: any) => api.post('/users', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowInvite(false); setError('')
      setSuccessMsg(`Invite email sent to ${variables.email} with a link to set up their password.`)
      setTimeout(() => setSuccessMsg(''), 8000)
      setForm({})
    },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to invite user') },
  })

  const updateUser = useMutation({
    mutationFn: ({ id, ...data }: any) => api.put(`/users/${id}`, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); setEditMode(false); setSelected(null); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to update user') },
  })

  const deleteUser = useMutation({
    mutationFn: (id: number) => api.delete(`/users/${id}`),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); setSelected(null); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to delete user') },
  })

  const openTransfer = async (member: User) => {
    setSelected(member); setShowTransfer(true); setOwnership(null); setReplacementUserId(''); setError('')
    try {
      const { data } = await api.get(`/users/${member.id}/ownership`)
      setOwnership(data)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load assigned work')
    }
  }

  const transferOwnership = useMutation({
    mutationFn: ({ id, replacementId, deleteAfter }: { id: number; replacementId: number; deleteAfter: boolean }) =>
      api.post(`/users/${id}/transfer-ownership`, {
        replacement_user_id: replacementId,
        delete_after_transfer: deleteAfter,
      }),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['teams'] })
      setShowTransfer(false); setSelected(null); setOwnership(null); setError('')
      setSuccessMsg(
        response.data?.deleted
          ? 'All active duties were transferred and the member was deleted.'
          : response.data?.retained_for_history
            ? 'All active duties were transferred. The member was deactivated instead of deleted to preserve submitted history.'
            : 'All active duties were transferred and the member was deactivated.'
      )
      setTimeout(() => setSuccessMsg(''), 8000)
    },
    onError: (err: any) => setError(err.response?.data?.detail || 'Ownership transfer failed'),
  })

  const resetPassword = useMutation({
    mutationFn: ({ id, new_password }: any) => api.post(`/users/${id}/reset-password`, { new_password }),
    onSuccess: () => { setShowReset(false); setNewPassword(''); setError('') },
    onError: (err: any) => { setError(err.response?.data?.detail || err.message || 'Failed to reset password') },
  })

  const savePermissions = useMutation({
    mutationFn: (grid: RolePermissionsGrid) => rolePermissionsApi.save(grid),
    onSuccess: (data) => {
      queryClient.setQueryData(['role-permissions'], (old: any) => old ? { ...old, grid: data.grid } : old)
      setLocalGrid(JSON.parse(JSON.stringify(data.grid)))
      setPermError('')
      setPermSaveMsg('Permissions saved successfully.')
      setTimeout(() => setPermSaveMsg(''), 5000)
    },
    onError: (err: any) => { setPermError(err.response?.data?.detail || 'Failed to save permissions') },
  })

  const resetPermissions = useMutation({
    mutationFn: () => rolePermissionsApi.reset(),
    onSuccess: (data) => {
      queryClient.setQueryData(['role-permissions'], (old: any) => old ? { ...old, grid: data.grid } : old)
      setLocalGrid(JSON.parse(JSON.stringify(data.grid)))
      setPermError('')
      setPermSaveMsg('Permissions reset to defaults.')
      setTimeout(() => setPermSaveMsg(''), 5000)
    },
    onError: (err: any) => { setPermError(err.response?.data?.detail || 'Failed to reset permissions') },
  })

  function togglePerm(role: string, key: string) {
    if (role === 'super_admin') return
    setLocalGrid(prev => {
      if (!prev) return prev
      return { ...prev, [role]: { ...prev[role], [key]: !prev[role]?.[key] } }
    })
  }

  if (isLoading) return (
    <div className="flex items-center justify-center h-32">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800"></div>
    </div>
  )

  if (usersError) return (
    <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
      <div className="text-red-600 font-semibold">Something went wrong</div>
      <div className="text-sm text-slate-500">Unable to load users. A database error occurred — please refresh or contact support.</div>
    </div>
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">User Management</h1>
        {activeTab === 'users' && (
          <div className="flex gap-2">
            <button onClick={() => { setShowCreate(true); setForm({ role: defaultRole }); setError('') }}
              className="flex items-center gap-2 px-4 py-2 bg-red-800 text-white rounded-lg text-sm font-medium hover:bg-red-900">
              <Plus size={16} /> Create User
            </button>
            <button onClick={() => { setShowInvite(true); setForm({ role: defaultRole }); setError('') }}
              className="flex items-center gap-2 px-4 py-2 bg-white text-red-800 border border-red-800 rounded-lg text-sm font-medium hover:bg-red-50">
              <Mail size={16} /> Invite User
            </button>
          </div>
        )}
      </div>

      <div className="flex gap-0 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'users' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          Users
        </button>
        <button
          onClick={() => { setActiveTab('teams'); setPermError('') }}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'teams' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <Users2 size={14} /> Teams
        </button>
        <button
          onClick={() => { setActiveTab('permissions'); setPermError('') }}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'permissions' ? 'border-red-800 text-red-800' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          <Shield size={14} /> Role Permissions
        </button>
      </div>

      {activeTab === 'users' && (
        <>
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
          {successMsg && <div className="flex items-center gap-2 bg-green-50 text-green-700 text-sm px-4 py-3 rounded-lg border border-green-200"><Mail size={16} className="shrink-0" /><span>{successMsg}</span></div>}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead><tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase">Username</th>
                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase">Full Name</th>
                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase">Email</th>
                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase">Role</th>
                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase">Team</th>
                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase">Status</th>
                <th className="text-left py-2.5 px-4 font-semibold text-slate-600 text-xs uppercase">Actions</th>
              </tr></thead>
              <tbody>
                {(users || []).map(u => {
                  const targetManageable = canManageUser(myRole, u.role)
                  return (
                    <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 px-4 font-medium text-slate-900">{u.username}</td>
                      <td className="py-2.5 px-4">{u.full_name}</td>
                      <td className="py-2.5 px-4 text-slate-500">{u.email || '-'}</td>
                      <td className="py-2.5 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE_COLORS[u.role] || 'bg-slate-100 text-slate-700'}`}>{ROLE_DISPLAY_NAMES[u.role] || u.role}</span>
                      </td>
                      <td className="py-2.5 px-4">
                        {u.team ? (
                          <button
                            onClick={() => setActiveTab('teams')}
                            className="text-xs text-red-700 hover:text-red-900 hover:underline font-medium"
                            title="Go to Teams tab"
                          >
                            {u.team.name}
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="flex gap-1">
                          {targetManageable ? (
                            <>
                              <button onClick={() => { setSelected(u); setEditMode(true); setForm({ full_name: u.full_name, email: u.email || '', role: u.role, is_active: u.is_active }); setError('') }}
                                className="px-2 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200">Edit</button>
                              <button onClick={() => { setSelected(u); setShowReset(true); setNewPassword(''); setError('') }}
                                className="px-2 py-1 text-xs bg-amber-50 text-amber-600 rounded hover:bg-amber-100"><Key size={12} /></button>
                               <button
                                 onClick={() => updateUser.mutate({ id: u.id, is_active: !u.is_active })}
                                 className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100">
                                 {u.is_active ? 'Deactivate' : 'Reactivate'}
                               </button>
                               <button onClick={() => openTransfer(u)}
                                 className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">Transfer / Delete</button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400 italic">No access</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'teams' && (
        <TeamsTab allUsers={users || []} />
      )}

      {activeTab === 'permissions' && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-sm text-slate-600">Configure what each role can do. Changes take effect immediately after saving.</p>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Lock size={11} /> Super Admin is always fully enabled and cannot be modified.
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={() => {
                  if (confirm('Reset all permissions to system defaults? This will overwrite any custom changes.')) {
                    resetPermissions.mutate()
                  }
                }}
                disabled={resetPermissions.isPending}
                className="flex items-center gap-1.5 px-3 py-2 text-sm border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <RotateCcw size={14} /> Reset to Defaults
              </button>
              <button
                onClick={() => { if (localGrid) savePermissions.mutate(localGrid) }}
                disabled={savePermissions.isPending || !localGrid}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50"
              >
                <Save size={14} /> {savePermissions.isPending ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>

          {permSaveMsg && (
            <div className="flex items-center gap-2 bg-green-50 text-green-700 text-sm px-4 py-3 rounded-lg border border-green-200">
              <CheckCircle2 size={16} className="shrink-0" /><span>{permSaveMsg}</span>
            </div>
          )}
          {permError && (
            <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
              <AlertCircle size={16} className="shrink-0" /><span>{permError}</span>
            </div>
          )}

          {(permLoading || !localGrid) ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-800"></div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="text-sm border-collapse" style={{ minWidth: '1120px' }}>
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="sticky left-0 z-10 bg-slate-50 text-left py-3 px-4 font-semibold text-slate-600 text-xs uppercase border-r border-slate-200" style={{ minWidth: '230px' }}>
                      Permission
                    </th>
                    {ALL_ROLES_LIST.map(role => (
                      <th key={role} className="py-3 px-1 text-center" style={{ minWidth: '66px' }}>
                        <div className="flex flex-col items-center gap-1">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-tight text-center ${ROLE_BADGE_COLORS[role]}`}>
                            {ROLE_DISPLAY_NAMES[role]}
                          </span>
                          {role === 'super_admin' && <Lock size={9} className="text-slate-400" />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSION_KEYS.map((key, idx) => (
                    <tr key={key} className={`border-b border-slate-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                      <td className="sticky left-0 z-10 bg-inherit py-2.5 px-4 text-xs font-medium text-slate-700 border-r border-slate-200 whitespace-nowrap">
                        {PERMISSION_LABELS[key] || key}
                      </td>
                      {ALL_ROLES_LIST.map(role => {
                        const isLocked = role === 'super_admin'
                        const checked = isLocked ? true : Boolean(localGrid[role]?.[key])
                        return (
                          <td key={role} className="py-2.5 px-1 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isLocked}
                              onChange={() => togglePerm(role, key)}
                              className="w-4 h-4 rounded border-slate-300 accent-red-800 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <SlideOver open={editMode && !!selected} onClose={() => { setEditMode(false); setSelected(null); setError('') }} title={`Edit ${selected?.username || ''}`}>
        <form onSubmit={e => { e.preventDefault(); if (selected) updateUser.mutate({ id: selected.id, ...form }) }} className="space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Full Name</label>
            <input value={form.full_name || ''} onChange={e => setForm({ ...form, full_name: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Username</label>
            <input value={form.username || ''} onChange={e => setForm({ ...form, username: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" placeholder="Leave blank to keep current" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Email</label>
            <input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" placeholder="user@example.com" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
            <select value={form.role || ''} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {assignableRoles.map(r => <option key={r} value={r}>{ROLE_DISPLAY_NAMES[r]}</option>)}</select></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.is_active ?? true} onChange={e => setForm({ ...form, is_active: e.target.checked })}
              className="rounded border-slate-300 text-red-800" /> Active
          </label>
          <button type="submit" className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900">Save Changes</button>
        </form>
      </SlideOver>

      <SlideOver open={showReset} onClose={() => { setShowReset(false); setError('') }} title={`Reset Password — ${selected?.username || ''}`}>
        <form onSubmit={e => { e.preventDefault(); if (selected) resetPassword.mutate({ id: selected.id, new_password: newPassword }) }} className="space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
          <div><label className="text-sm font-medium text-slate-700 block mb-1">New Password</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg" required minLength={6} /></div>
          <button type="submit" className="w-full py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700">Reset Password</button>
        </form>
      </SlideOver>

      <SlideOver open={showTransfer} onClose={() => { setShowTransfer(false); setSelected(null); setOwnership(null); setError('') }} title={`Transfer duties — ${selected?.full_name || ''}`}>
        <div className="space-y-4">
          {error && <div className="flex items-start gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0 mt-0.5" /><span>{error}</span></div>}
          {!ownership ? (
            !error && <div className="text-sm text-slate-500 py-8 text-center">Checking assigned work…</div>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                <strong>{ownership.total} active assignment{ownership.total === 1 ? '' : 's'}</strong> will be transferred. Historical reports, comments, approvals, and creator records will keep the original member’s name.
              </div>
              {ownership.preserved_total > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                  {ownership.preserved_total} weekly report draft{ownership.preserved_total === 1 ? '' : 's'} will remain with the original member and will not be transferred.
                </div>
              )}
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {ownership.categories.filter((c: any) => c.count > 0).map((category: any) => (
                  <div key={category.key} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-slate-700">{category.label}</span>
                    <span className="font-semibold text-slate-900">{category.count}</span>
                  </div>
                ))}
                {ownership.total === 0 && <div className="px-3 py-4 text-sm text-slate-500 text-center">No active assignments found.</div>}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">Transfer active duties to</label>
                <select value={replacementUserId} onChange={e => setReplacementUserId(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white">
                  <option value="">Choose an active member…</option>
                  {(users || []).filter(u => u.is_active && u.id !== selected?.id).map(u => (
                    <option key={u.id} value={u.id}>{u.full_name} — {ROLE_DISPLAY_NAMES[u.role]}</option>
                  ))}
                </select>
              </div>
              <button
                disabled={!replacementUserId || transferOwnership.isPending}
                onClick={() => selected && transferOwnership.mutate({ id: selected.id, replacementId: Number(replacementUserId), deleteAfter: false })}
                className="w-full py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50">
                {transferOwnership.isPending ? 'Transferring…' : 'Transfer & Deactivate'}
              </button>
              <button
                disabled={!replacementUserId || transferOwnership.isPending}
                onClick={() => {
                  if (selected && confirm(`Permanently delete ${selected.full_name} after transferring all active duties? Historical attribution will be preserved.`)) {
                    transferOwnership.mutate({ id: selected.id, replacementId: Number(replacementUserId), deleteAfter: true })
                  }
                }}
                className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
                Transfer & Permanently Delete
              </button>
            </>
          )}
        </div>
      </SlideOver>

      <SlideOver open={showCreate} onClose={() => { setShowCreate(false); setError('') }} title="Create User">
        <form onSubmit={e => { e.preventDefault(); createUserDirect.mutate(form) }} className="space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
          <div className="bg-blue-50 text-blue-700 text-sm px-4 py-3 rounded-lg border border-blue-200">
            Create a user account directly with a username and password. No email verification required.
          </div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Username *</label>
            <input value={form.username || ''} onChange={e => setForm({ ...form, username: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Full Name *</label>
            <input value={form.full_name || ''} onChange={e => setForm({ ...form, full_name: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Password *</label>
            <input type="password" value={form.password || ''} onChange={e => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required minLength={6} placeholder="Min 6 characters" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Email</label>
            <input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" placeholder="Optional" /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
            <select value={form.role || defaultRole} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {assignableRoles.map(r => <option key={r} value={r}>{ROLE_DISPLAY_NAMES[r]}</option>)}</select></div>
          <button type="submit" disabled={createUserDirect.isPending} className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
            {createUserDirect.isPending ? 'Creating...' : 'Create User'}
          </button>
        </form>
      </SlideOver>

      <SlideOver open={showInvite} onClose={() => { setShowInvite(false); setError('') }} title="Invite User">
        <form onSubmit={e => { e.preventDefault(); inviteUser.mutate(form) }} className="space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200"><AlertCircle size={16} className="shrink-0" /><span>{error}</span></div>}
          <div className="bg-blue-50 text-blue-700 text-sm px-4 py-3 rounded-lg border border-blue-200">
            An invite email will be sent with a link for the user to create their own password.
          </div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Username *</label>
            <input value={form.username || ''} onChange={e => setForm({ ...form, username: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Full Name *</label>
            <input value={form.full_name || ''} onChange={e => setForm({ ...form, full_name: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Email *</label>
            <input type="email" value={form.email || ''} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg" placeholder="user@example.com" required /></div>
          <div><label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
            <select value={form.role || defaultRole} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-lg">
              {assignableRoles.map(r => <option key={r} value={r}>{ROLE_DISPLAY_NAMES[r]}</option>)}</select></div>
          <button type="submit" disabled={inviteUser.isPending} className="w-full py-2 text-sm bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50">
            {inviteUser.isPending ? 'Sending Invite...' : 'Send Invite'}
          </button>
        </form>
      </SlideOver>
    </div>
  )
}
